import { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { X, Circle, Square, Loader2, Copy, Check, Maximize, Minimize } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { videosAPI, projectsAPI } from '../lib/api';
import { toast } from 'sonner';

const FullscreenPlayer = ({ open, onClose, videoUrl, videoName, isVideoElement = false }) => {
  const { token, user } = useAuth();
  const [canRecord, setCanRecord] = useState(false);
  const [userProjectId, setUserProjectId] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);
  const containerRef = useRef(null);
  const autoUploadRef = useRef(false);

  // Check if user is pilot with authorized project (has_videos)
  useEffect(() => {
    if (!open || user?.role !== 'pilot') {
      setCanRecord(false);
      return;
    }
    const checkProject = async () => {
      try {
        const res = await projectsAPI.list();
        const pid = user.project_id;
        if (pid) {
          const proj = res.data.find(p => p.id === pid && p.has_videos);
          if (proj) {
            setCanRecord(true);
            setUserProjectId(proj.id);
            return;
          }
        }
        setCanRecord(false);
        setUserProjectId(null);
      } catch {
        setCanRecord(false);
      }
    };
    checkProject();
  }, [open, user?.role, user?.project_id]);

  // Enter native fullscreen on open
  useEffect(() => {
    if (open && containerRef.current) {
      const el = containerRef.current;
      const enterFS = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen || el.mozRequestFullScreen;
      if (enterFS) {
        enterFS.call(el).then(() => setIsNativeFullscreen(true)).catch(() => {});
      }
    }
  }, [open]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handler = () => {
      const isFS = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      setIsNativeFullscreen(isFS);
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen).call(document);
    } else {
      const enterFS = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (enterFS) enterFS.call(el).catch(() => {});
    }
  }, []);

  // Auto-upload recording to user's project
  const doAutoUpload = useCallback(async (blob) => {
    if (!blob || !userProjectId) return;
    setUploading(true);
    setUploadProgress(0);

    const now = new Date();
    const fileName = `gravacao_${now.toISOString().slice(0,10)}_${now.toTimeString().slice(0,5).replace(':','h')}.webm`;
    const file = new File([blob], fileName, { type: 'video/webm' });
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await videosAPI.upload(userProjectId, formData, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      });
      setUploadResult(res.data);
      toast.success('Gravacao enviada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar gravacao');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [userProjectId]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        surfaceSwitching: 'exclude',
        monitorTypeSurfaces: 'exclude'
      });
      streamRef.current = stream;
      chunksRef.current = [];
      autoUploadRef.current = false;

      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }

      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2500000 });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        clearInterval(timerRef.current);
        // Auto-upload immediately
        if (!autoUploadRef.current) {
          autoUploadRef.current = true;
          doAutoUpload(blob);
        }
      };

      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state === 'recording') recorder.stop();
        setRecording(false);
        clearInterval(timerRef.current);
      };

      recorder.start(1000);
      setRecording(true);
      setRecordingTime(0);
      setUploadResult(null);

      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);

      toast.success('Gravacao iniciada!');
    } catch (err) {
      if (err.name !== 'NotAllowedError') {
        toast.error('Erro ao iniciar gravacao');
        console.error('Recording error:', err);
      }
    }
  }, [doAutoUpload]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    clearInterval(timerRef.current);
  }, []);

  const handleClose = () => {
    if (recording) stopRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    clearInterval(timerRef.current);
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen).call(document).catch(() => {});
    }
    setUploadResult(null);
    setRecording(false);
    setRecordingTime(0);
    autoUploadRef.current = false;
    onClose();
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const getShareUrl = (video) => `${window.location.origin}/video/${video.share_token}`;
  const copyLink = (video) => {
    navigator.clipboard.writeText(getShareUrl(video));
    setLinkCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-[100vw] max-h-[100vh] w-screen h-screen bg-black border-none p-0 rounded-none" data-testid="fullscreen-player">
        <DialogTitle className="sr-only">{videoName || 'Player'}</DialogTitle>
        <div ref={containerRef} className="relative w-full h-full bg-black" style={{ position: 'relative' }}>
          
          {/* Close button */}
          <Button
            variant="ghost" size="icon"
            className="absolute top-3 right-3 z-30 bg-black/60 hover:bg-red-600/40 text-white hover:text-red-400 h-10 w-10 rounded-full"
            onClick={handleClose}
            data-testid="fullscreen-close-btn"
          >
            <X size={20} />
          </Button>

          {/* Fullscreen toggle */}
          <Button
            variant="ghost" size="icon"
            className="absolute top-3 right-16 z-30 bg-black/60 hover:bg-white/20 text-white h-10 w-10 rounded-full"
            onClick={toggleFullscreen}
            data-testid="fullscreen-toggle-btn"
          >
            {isNativeFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </Button>

          {/* Content */}
          {isVideoElement ? (
            <video src={videoUrl} className="w-full h-full object-contain" controls autoPlay />
          ) : (
            <iframe src={videoUrl} className="w-full h-full border-none" allowFullScreen title={videoName || 'Stream'} allow="autoplay; fullscreen" />
          )}

          {/* Bottom controls bar */}
          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/90 via-black/50 to-transparent pt-12 pb-4 px-4">
            <div className="flex items-center justify-between">
              <span className="text-white/80 text-xs font-mono truncate max-w-[40%]">{videoName || 'Stream'}</span>

              <div className="flex items-center gap-2">
                {recording && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-full animate-pulse" data-testid="recording-indicator">
                    <Circle size={8} className="text-white fill-white" />
                    <span className="text-white text-xs font-mono font-bold">{formatTime(recordingTime)}</span>
                  </div>
                )}

                {canRecord && !isVideoElement && !uploading && !uploadResult && (
                  recording ? (
                    <Button
                      onClick={stopRecording}
                      className="bg-red-600 hover:bg-red-700 text-white rounded-full h-10 px-5 text-xs gap-2 font-bold shadow-lg shadow-red-900/30"
                      data-testid="stop-recording-btn"
                    >
                      <Square size={14} className="fill-white" /> PARAR GRAVACAO
                    </Button>
                  ) : (
                    <Button
                      onClick={startRecording}
                      className="bg-white/15 hover:bg-white/25 text-white rounded-full h-10 px-5 text-xs gap-2 backdrop-blur-md border border-white/20 font-bold"
                      data-testid="start-recording-btn"
                    >
                      <Circle size={14} className="text-red-500 fill-red-500" /> GRAVAR
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Auto-uploading indicator */}
            {uploading && (
              <div className="mt-3 bg-black/70 backdrop-blur-md rounded-xl p-4 border border-white/10" data-testid="recording-uploading-panel">
                <div className="flex items-center gap-3">
                  <Loader2 size={16} className="text-gold animate-spin" />
                  <span className="text-white text-sm font-semibold">Enviando gravacao...</span>
                  <span className="text-gold font-mono font-bold text-sm">{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-gold rounded-full transition-all duration-300 ease-out" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}

            {/* Upload complete: share link */}
            {uploadResult && (
              <div className="mt-3 bg-black/70 backdrop-blur-md rounded-xl p-4 border border-green-500/30" data-testid="recording-share-panel">
                <p className="text-green-400 text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <Check size={16} /> Gravacao enviada com sucesso!
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white/10 rounded-lg px-3 py-2 border border-white/10 overflow-hidden">
                    <p className="text-white/70 text-[10px] font-mono truncate" data-testid="recording-share-url">
                      {getShareUrl(uploadResult)}
                    </p>
                  </div>
                  <Button
                    onClick={() => copyLink(uploadResult)}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-lg h-9 px-4 text-xs gap-1.5 font-bold"
                    data-testid="copy-recording-link-btn"
                  >
                    {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                    {linkCopied ? 'COPIADO' : 'COPIAR LINK'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FullscreenPlayer;
